import axios from 'axios';
import store from '@/store';
import { router } from '@/main';

const baseURL = process.env.VUE_APP_API_URL;

const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;

    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

const apiRequest = async (method, url, data = null, params = {}) => {
    if (store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    return store.dispatch('user/apiCall', async (token) => {
        const config = {
            method: method,
            url: `${baseURL}/accounts/${store.state.user.user.account_id}/${url}`,
            headers: getAuthHeader(token),
            params: { ...params },
            data: data,
        };
        return await axios(config);
    });
};

/**
 * Agent profiles API.
 *
 * A profile is a named variant of the account's agent: its own instructions,
 * optional overrides of tone/language/gender/timezone (null = inherit from the
 * account agent settings) and its own view of the installed skills, with
 * per-profile config overrides (e.g. a second set of Restoo credentials).
 * Channels bind to a profile; conversations opened through a channel are
 * answered by that profile. Every account has one default profile, which
 * cannot be deleted nor disabled.
 */
const agentProfilesService = {
    /** Lists the account's profiles (the default one is created lazily, and comes first). */
    fetchAgentProfiles() {
        return apiRequest('get', `agent-profiles?ts=${new Date().getTime()}`);
    },

    getAgentProfile(profileId) {
        return apiRequest('get', `agent-profiles/${profileId}`);
    },

    /**
     * @param {Object} data { name, description, instructions, conversation_tone, language, gender, timezone, enabled }
     */
    createAgentProfile(data) {
        return apiRequest('post', 'agent-profiles', data);
    },

    /**
     * Partial update: null keeps the value. For the overrides (tone, language,
     * gender, timezone) send '' to go back to inheriting the account value.
     */
    updateAgentProfile(profileId, data) {
        return apiRequest('post', `agent-profiles/${profileId}`, data);
    },

    deleteAgentProfile(profileId) {
        return apiRequest('delete', `agent-profiles/${profileId}`);
    },

    /**
     * Links an installed account skill to the profile, or replaces the link's
     * overrides. configValues replaces the previous overrides whole.
     */
    linkSkill(profileId, skillId, configValues = {}, enabled = true) {
        return apiRequest('post', `agent-profiles/${profileId}/skills/${skillId}`, {
            enabled,
            config_values: configValues || {},
        });
    },

    unlinkSkill(profileId, skillId) {
        return apiRequest('delete', `agent-profiles/${profileId}/skills/${skillId}`);
    },

    /** Binds a channel to a profile: new conversations on it are answered by that profile. */
    assignProfileToChannel(channelId, profileId) {
        return apiRequest('post', `communication-channels/${channelId}/agent-profile/${profileId}`);
    },

    /** Clears the channel binding: the channel goes back to the default profile. */
    resetChannelProfile(channelId) {
        return apiRequest('delete', `communication-channels/${channelId}/agent-profile`);
    },

    /** Scope endpoints for knowledge (empty list = visible to every profile). No retraining. */
    setQuestionAnswerProfiles(questionAnswerId, profileIds) {
        return apiRequest('post', `questions-answers/${questionAnswerId}/profiles`, { profile_ids: profileIds || [] });
    },

    setDecisionFlowProfiles(flowId, profileIds) {
        return apiRequest('post', `decision-flows/${flowId}/profiles`, { profile_ids: profileIds || [] });
    },

    /** Escalation rules are keyed by their escalate_to_human config key (`rule_<uuid>`). */
    setEscalationRuleProfiles(ruleKey, profileIds) {
        return apiRequest('post', `knowledge-rules/escalations/${ruleKey}/profiles`, { profile_ids: profileIds || [] });
    },

    setBusinessKnowledgeProfiles(businessKnowledgeId, profileIds) {
        return apiRequest('post', `business-knowledges/${businessKnowledgeId}/profiles`, {
            profile_ids: profileIds || [],
        });
    },
};

export default agentProfilesService;
